/**
 * Detects whether the given `element` or one of its ancestors has the
 * "Children Presentational: True" characteristic, and is thus excluded from
 * the accessibility tree.
 *
 * If this function returns true for an element, that element's contents will
 * not be read by most screenreaders. However, an aria-label on the element
 * may be read.
 *
 * @see https://www.w3.org/TR/wai-aria-1.2/#tree_exclusion
 *
 * @param element
 */
export function isPresentationalForSR(element: HTMLElement | null): boolean {
    if (element == null) {
        return false;
    }
    return (
        presentationalRoles.some((role) => element.matches(`[role=${role}]`)) ||
        isPresentationalForSR(element.parentElement)
    );
}

/**
 * List of aria-roles that apply "presentation" to descendants, which strips
 * math markup of its semantic meaning, which makes it inaccessible to screen
 * readers and other assistive technology.
 *
 * Taken from this rule from WAI-ARIA 1.2:
 * https://www.w3.org/TR/wai-aria-1.2/#tree_exclusion
 */
const presentationalRoles = [
    "button",
    "checkbox",
    "img",
    "menuitemcheckbox",
    "menuitemradio",
    "meter",
    "option",
    "progressbar",
    "radio",
    "scrollbar",
    "separator",
    "slider",
    "switch",
    "tab",
    // "combobox" is NOT included in the WAI-ARIA 1.2 list of aria-roles that
    // apply "presentation" to descendants, but screenreaders seem to treat it
    // as presentational anyway. For context, see:
    // - https://github.com/Khan/mathjax-renderer/commit/d706d27eab482f32d0d1c93f23fb6af6d1add8cf
    // - https://khanacademy.atlassian.net/browse/LIT-1425
    "combobox",
] as const;
